import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHead, Badge } from "../../components/ui-bits";
import { toast } from "sonner";
import { CaretDown, CaretUp, SignIn, Buildings, Phone, MapPin, Receipt } from "@phosphor-icons/react";

const PLANS = ["Starter", "Growth", "Professional", "Enterprise"];
const SERVICES = ["GST", "ITR", "TDS", "ROC", "Audit", "Payroll", "CMA", "Bookkeeping", "Advisory"];

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sel, setSel] = useState({});
  const [openRow, setOpenRow] = useState(null);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const load = () => {
    api.get("/admin/clients").then((r) => setClients(r.data));
    api.get("/admin/agents").then((r) => setAgents(r.data));
  };
  useEffect(() => { load(); }, []);

  const assign = async (client) => {
    const agent_email = sel[client.email];
    if (!agent_email) { toast.error("Pick an agent first"); return; }
    const { data } = await api.post("/admin/assign", { client_email: client.email, agent_email });
    toast.success(`Assigned ${data.agent_name} to ${client.company}`);
    load();
  };

  const changePlan = async (client, plan) => {
    await api.patch(`/admin/clients/${encodeURIComponent(client.email)}`, { plan });
    toast.success(`${client.company} → ${plan}`);
    load();
  };

  const toggleService = async (client, svc) => {
    const current = client.services || [];
    const next = current.includes(svc) ? current.filter((s) => s !== svc) : [...current, svc];
    await api.patch(`/admin/clients/${encodeURIComponent(client.email)}`, { services: next });
    toast.success(`Services updated for ${client.company}`);
    load();
  };

  const toggleStatus = async (client) => {
    const next = !client.disabled;
    await api.patch(`/admin/users/${encodeURIComponent(client.email)}/status`, { disabled: next });
    toast.success(`${client.company} login ${next ? "disabled" : "enabled"}`);
    load();
  };

  const openPortal = async (client) => {
    try {
      const { data } = await api.post("/admin/impersonate", { email: client.email });
      setUser(data);
      toast.success(`Opening ${client.company}'s portal…`);
      navigate("/");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not open portal"); }
  };

  return (
    <div>
      <PageHead eyebrow="Client Management" title="Clients" />
      <div className="card p-6 overflow-x-auto">
        <table className="w-full text-sm min-w-[960px]">
          <thead><tr className="label text-left border-b border-black/10">
            <th className="pb-2 w-8"></th><th className="pb-2">Business</th><th className="pb-2">Contact</th>
            <th className="pb-2">Plan</th><th className="pb-2">Assigned Agent</th><th className="pb-2">Login</th>
            <th className="pb-2 text-right">Actions</th></tr></thead>
          <tbody>
            {clients.map((c, i) => (
              <Fragment key={c.email}>
                <tr data-testid={`aclient-${i}`} className="border-b border-black/5">
                  <td className="py-3">
                    <button data-testid={`aclient-expand-${i}`} onClick={() => setOpenRow(openRow === c.email ? null : c.email)} className="text-[#888] hover:text-[#002FA7]">
                      {openRow === c.email ? <CaretUp size={16} /> : <CaretDown size={16} />}
                    </button>
                  </td>
                  <td className="py-3 font-medium">{c.company}
                    {c.disabled && <span className="ml-2 text-[10px] uppercase font-bold text-[#FF3B30]">Disabled</span>}</td>
                  <td className="py-3 text-[#555]">{c.name}<br /><span className="text-xs text-[#888]">{c.email}</span></td>
                  <td className="py-3">
                    <select data-testid={`plan-${i}`} value={c.plan || ""} onChange={(e) => changePlan(c, e.target.value)}
                      className="border border-black/20 rounded-sm px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                      <option value="">—</option>
                      {PLANS.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="py-3">{c.assigned_agent_name
                    ? <Badge status="completed">{c.assigned_agent_name}</Badge>
                    : <span className="text-xs text-[#888]">Unassigned</span>}</td>
                  <td className="py-3">
                    <button data-testid={`client-toggle-${i}`} onClick={() => toggleStatus(c)} role="switch" aria-checked={!c.disabled}
                      className={`relative w-11 h-6 rounded-full transition-colors ${c.disabled ? "bg-black/15" : "bg-[#00A86B]"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${c.disabled ? "" : "translate-x-5"}`} />
                    </button>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center gap-2 justify-end flex-wrap">
                      <select data-testid={`assign-select-${i}`} value={sel[c.email] || ""} onChange={(e) => setSel({ ...sel, [c.email]: e.target.value })}
                        className="border border-black/20 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                        <option value="">Select agent…</option>
                        {agents.map((a) => <option key={a.email} value={a.email}>{a.name} ({a.clients ?? 0} clients)</option>)}
                      </select>
                      <button data-testid={`assign-btn-${i}`} onClick={() => assign(c)}
                        className="bg-[#111] hover:bg-black text-white text-xs px-3 py-1.5 rounded-sm transition-colors">Assign</button>
                      <button data-testid={`client-portal-${i}`} onClick={() => openPortal(c)} disabled={c.disabled}
                        className="bg-[#002FA7] hover:bg-[#00227A] disabled:opacity-40 text-white text-xs px-3 py-1.5 rounded-sm inline-flex items-center gap-1 transition-colors">
                        <SignIn size={13} /> Open Portal</button>
                    </div>
                  </td>
                </tr>
                {openRow === c.email && (
                  <tr data-testid={`aclient-detail-${i}`} className="bg-black/[0.02] border-b border-black/5">
                    <td></td>
                    <td colSpan={6} className="py-4">
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <Detail Icon={Buildings} label="Business Type" value={c.business?.business_type} />
                        <Detail Icon={Receipt} label="GSTIN" value={c.business?.gstin} />
                        <Detail Icon={Phone} label="Phone" value={c.business?.phone} />
                        <Detail Icon={MapPin} label="City" value={c.business?.city} />
                        <Detail label="Annual Turnover" value={c.business?.annual_turnover} />
                        <Detail label="Role" value={c.role} />
                        <Detail label="Member Since" value={(c.created_at || "").slice(0, 10)} />
                        <Detail label="Assigned Agent Email" value={c.assigned_agent_email} />
                      </div>
                      <div className="mt-4">
                        <p className="label mb-2">Services Subscribed (click to toggle)</p>
                        <div className="flex flex-wrap gap-2">
                          {SERVICES.map((svc) => {
                            const on = (c.services || []).includes(svc);
                            return (
                              <button key={svc} data-testid={`svc-toggle-${i}-${svc}`} onClick={() => toggleService(c, svc)}
                                className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${on ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-black/20 hover:bg-black/5"}`}>
                                {svc}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Detail({ Icon, label, value }) {
  return (
    <div>
      <p className="label mb-0.5 flex items-center gap-1">{Icon && <Icon size={12} />} {label}</p>
      <p className="font-medium">{value || <span className="text-[#bbb]">—</span>}</p>
    </div>
  );
}
